import { scheduleToDays, toDisplayDayIndex } from '@/domain/templates/day-of-week';
import { fixedDayCount, type DayCountResolver } from '@/domain/templates/staffing';
import type { ShiftSet } from '@/types/template';

/** One rendered shift pill in a Mon–Sun preview grid (one per required head). */
export interface PreviewPill {
  key: string;
  type: string;
  time: string;
  draft: boolean;
  /** Nth occurrence of this shift type among the shifts (for overlay shading). */
  variant: number;
}

/**
 * A day's column in the per-head preview: the pills to render (capped at
 * {@link MAX_PREVIEW_PILLS_PER_DAY}) plus the **true** total head count. The cap
 * stops a runaway demand-scaled day (e.g. a huge forecast with no max cap) from
 * expanding into thousands of DOM nodes and freezing/crashing the browser; the
 * grid shows the remainder as a "+N more" chip and the real total in the header.
 */
export interface PreviewColumn {
  pills: PreviewPill[];
  total: number;
}

/** Max per-head pills rendered for a single day before the rest collapse into "+N more". */
export const MAX_PREVIEW_PILLS_PER_DAY = 60;

/**
 * Maps each shift id to the Nth occurrence of its shift type across the given
 * shifts, so repeats of the same type render with a progressively stronger
 * overlay. Computed from the full shift list so shading stays consistent across
 * preview views (shift-type grid and staff-group sections).
 */
export function computeVariantByShiftId(shifts: ShiftSet[]): Map<string, number> {
  const variantByShiftId = new Map<string, number>();
  const seenByType = new Map<string, number>();
  for (const shift of shifts) {
    const seen = seenByType.get(shift.shiftType.name) ?? 0;
    variantByShiftId.set(shift.id, seen);
    seenByType.set(shift.shiftType.name, seen + 1);
  }
  return variantByShiftId;
}

/**
 * Expands shifts (and an optional draft) into per-display-day pill lists: one
 * pill per required head on each ISO day the shift runs. Draft pills always use
 * variant 0; saved pills read their variant from `variantByShiftId`. `resolveCount`
 * defaults to the fixed staffing count; pass a scaling resolver to vary the head
 * count per day.
 */
export function buildShiftColumns(
  shifts: ShiftSet[],
  draftShift: ShiftSet | null,
  variantByShiftId: Map<string, number>,
  resolveCount: DayCountResolver = fixedDayCount,
): PreviewColumn[] {
  const columns: PreviewColumn[] = Array.from({ length: 7 }, () => ({ pills: [], total: 0 }));

  const push = (shift: ShiftSet, draft: boolean) => {
    const time = `${shift.startTime}–${shift.endTime}`;
    const variant = draft ? 0 : (variantByShiftId.get(shift.id) ?? 0);
    for (const dayOfWeek of scheduleToDays(shift.schedule)) {
      const dayIndex = toDisplayDayIndex(dayOfWeek);
      const heads = resolveCount(shift, dayOfWeek);
      const column = columns[dayIndex];
      column.total += heads;
      // Only materialise pills up to the per-day cap; the remainder is conveyed
      // by `total` (header count) and the grid's "+N more" chip.
      for (let head = 0; head < heads && column.pills.length < MAX_PREVIEW_PILLS_PER_DAY; head++) {
        column.pills.push({
          key: `${draft ? 'draft' : shift.id}-${dayOfWeek}-${head}`,
          type: shift.shiftType.name,
          time,
          draft,
          variant,
        });
      }
    }
  };

  for (const shift of shifts) push(shift, false);
  if (draftShift) push(draftShift, true);

  return columns;
}
